import { Topic } from "roslib";
import type { Ros } from "roslib";

import { rosTopicMessageType } from "lib/consts/rosTopicTypes";

/** Poll interval while waiting for a topic to appear on rosbridge. */
export const ROS_TOPIC_POLL_MS = 500;

/** Re-attempt subscribe while no message has been received yet. */
export const ROS_TOPIC_RETRY_WHILE_MISSING_MS = 3000;

/** Stop `getTopicType` polling after this many empty lookups (0 = unlimited). */
export const ROS_TOPIC_MAX_TYPE_POLLS = 8;

/** Re-subscribe interval when connected but no usable message yet (known message type). */
export const ROS_TOPIC_RESUBSCRIBE_WHILE_MISSING_MS = 15_000;

export type RosControlSocketLike = {
    subscribe: (topic: string, handler: (data: unknown) => void) => void;
    unsubscribe: (topic: string, handler: (data: unknown) => void) => void;
    socket?: Ros;
};

export type RosSubscribeQos = {
    durability?: string;
    reliability?: string;
    history?: string;
    depth?: number;
};

export type AttachRosTopicOptions = {
    /** Delay before the first subscribe attempt (e.g. rosbridge warm-up). */
    initialDelayMs?: number;
    pollIntervalMs?: number;
    retryWhileMissingMs?: number;
    /** Periodically re-subscribe until the first message arrives. Default true. */
    retryUntilFirstMessage?: boolean;
    /**
     * When subscribed but `markMessageReceived` has not fired, tear down and
     * re-subscribe on this interval (0 = keep single subscription only).
     */
    resubscribeWhileMissingMs?: number;
    /** Used when `getTopicType` fails (topic exists but is not listed yet). */
    messageTypeFallback?: string;
    /** rosbridge v2.1+ subscribe QoS (e.g. transient_local for latched state). */
    subscribeQos?: RosSubscribeQos;
    /** Cap repeated `getTopicType` polls when a topic is not advertised (0 = unlimited). */
    maxTopicTypePolls?: number;
    /** rosbridge compression mode for the Topic subscription. */
    compression?: "none" | "cbor" | "cbor-raw";
    /**
     * Return true when the handler considers the topic value usable.
     * When omitted, any message stops retry (including WebROSProxy emits).
     */
    markMessageReceived?: (data: unknown) => boolean;
};

/** Shared options for topics that may appear after connect (battery, gimbal, etc.). */
export const LATE_ROS_TOPIC_OPTIONS: AttachRosTopicOptions = {
    retryUntilFirstMessage: true,
    resubscribeWhileMissingMs: ROS_TOPIC_RESUBSCRIBE_WHILE_MISSING_MS,
};

const isPrimitiveType = (value: unknown): value is string | number | boolean =>
    typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const unNestDataFieldIfPrimitive = (msg: unknown): unknown => {
    if (msg == null || typeof msg !== "object") {
        return msg;
    }
    const record = msg as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length === 1 && keys[0] === "data" && isPrimitiveType(record.data)) {
        return record.data;
    }
    return msg;
};

/**
 * True only for the *real* roslib `Ros`, which we drive via roslib `Topic` (needs
 * `callOnConnection`). NOTE: the vendored Foxglove client's `Ros` also exposes
 * `getTopicType`, so `getTopicType` alone is not sufficient to identify roslib —
 * checking `callOnConnection` (roslib-only) keeps the Foxglove socket on the
 * `controlSocket.subscribe` path below instead of crashing in a roslib `Topic`.
 */
function isRosLibJsSocket(
    controlSocket: RosControlSocketLike,
): controlSocket is RosControlSocketLike & { socket: Ros } {
    const ros = controlSocket.socket as (Ros & { callOnConnection?: unknown }) | undefined;
    return (
        ros != null &&
        typeof ros.getTopicType === "function" &&
        typeof ros.callOnConnection === "function"
    );
}

/**
 * True when the socket can resolve a topic's message type by name — the real roslib
 * `Ros` or the Foxglove client's `Ros` (both expose `getTopicType`). Used for topic
 * discovery, which is safe on either (no `Topic` construction involved).
 */
function hasTopicTypeLookup(
    controlSocket: RosControlSocketLike,
): controlSocket is RosControlSocketLike & { socket: Ros } {
    const ros = controlSocket.socket;
    return ros != null && typeof ros.getTopicType === "function";
}

function getTopicType(ros: Ros, topic: string): Promise<string> {
    return new Promise((resolve) => {
        ros.getTopicType(
            topic,
            (type: string) => resolve(type || ""),
            () => resolve(""),
        );
    });
}

export type ResolveAdvertisedRosTopicOptions = {
    pollIntervalMs?: number;
    /** Total poll rounds across all candidates (0 = single pass). */
    maxPollRounds?: number;
};

/**
 * Pick the first advertised topic from `topics` (in priority order).
 * Falls back to `topics[0]` when none are listed yet or topic lookup is unavailable.
 */
export async function resolveAdvertisedRosTopic(
    controlSocket: RosControlSocketLike,
    topics: readonly string[],
    options: ResolveAdvertisedRosTopicOptions = {},
): Promise<string> {
    const candidates = topics.filter((topic) => topic.length > 0);
    if (candidates.length === 0) {
        return "";
    }

    if (!hasTopicTypeLookup(controlSocket)) {
        return candidates[0];
    }

    const { pollIntervalMs = ROS_TOPIC_POLL_MS, maxPollRounds = ROS_TOPIC_MAX_TYPE_POLLS } =
        options;
    const ros = controlSocket.socket;
    const rounds = maxPollRounds > 0 ? maxPollRounds : 1;

    for (let round = 0; round < rounds; round++) {
        for (const topic of candidates) {
            const messageType = await getTopicType(ros, topic);
            if (messageType) {
                return topic;
            }
        }
        if (round < rounds - 1) {
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        }
    }

    return candidates[0];
}

function attachSubscribeQos(topic: Topic, qos: RosSubscribeQos): void {
    const originalSend = topic.callForSubscribeAndAdvertise.bind(topic);
    topic.callForSubscribeAndAdvertise = (D: { op?: string }) => {
        if (D.op === "subscribe") {
            originalSend({ ...D, qos });
        } else {
            originalSend(D);
        }
    };
}

/**
 * Subscribe to a ROS topic with deferred setup and retry while connected.
 * Bypasses arcjs `ROSLibJSControlSource.subscribe` race conditions by using
 * roslib `Topic` directly and polling `getTopicType` until the topic exists.
 */
export function attachRosTopicWithRetry(
    controlSocket: RosControlSocketLike,
    topic: string,
    handler: (data: unknown) => void,
    options: AttachRosTopicOptions = {},
): () => void {
    const {
        initialDelayMs = 0,
        pollIntervalMs = ROS_TOPIC_POLL_MS,
        retryWhileMissingMs = ROS_TOPIC_RETRY_WHILE_MISSING_MS,
        retryUntilFirstMessage = true,
        resubscribeWhileMissingMs = 0,
        messageTypeFallback = rosTopicMessageType(topic),
        subscribeQos,
        maxTopicTypePolls = messageTypeFallback ? 0 : ROS_TOPIC_MAX_TYPE_POLLS,
        compression = "cbor",
        markMessageReceived,
    } = options;

    let cancelled = false;
    let topicListener: Topic | null = null;
    let proxyHandler: ((data: unknown) => void) | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let missingRetryTimer: ReturnType<typeof setInterval> | null = null;
    let hasReceivedMessage = false;
    let subscribeInFlight = false;
    let topicTypePollCount = 0;
    let topicTypePollingExhausted = false;
    let lastSubscribedAt = 0;

    const clearMissingRetryTimer = () => {
        if (missingRetryTimer != null) {
            clearInterval(missingRetryTimer);
            missingRetryTimer = null;
        }
    };

    const wrappedHandler = (data: unknown) => {
        handler(data);
        const received = (markMessageReceived ?? (() => true))(data);
        if (received) {
            hasReceivedMessage = true;
            clearMissingRetryTimer();
        }
    };

    const teardownListener = () => {
        if (topicListener) {
            topicListener.unsubscribe();
            topicListener = null;
        }
        if (proxyHandler) {
            try {
                controlSocket.unsubscribe(topic, proxyHandler);
            } catch {
                // Subscription may already be removed.
            }
            proxyHandler = null;
        }
    };

    const schedulePoll = () => {
        if (
            cancelled ||
            hasReceivedMessage ||
            pollTimer != null ||
            topicTypePollingExhausted
        ) {
            return;
        }
        pollTimer = setTimeout(() => {
            pollTimer = null;
            if (!cancelled && !hasReceivedMessage) {
                void trySubscribe();
            }
        }, pollIntervalMs);
    };

    const trySubscribe = async () => {
        if (cancelled || hasReceivedMessage || subscribeInFlight) {
            return;
        }

        subscribeInFlight = true;
        try {
            if (isRosLibJsSocket(controlSocket)) {
                const ros = controlSocket.socket;
                let messageType = messageTypeFallback ?? "";
                if (!messageType && !topicTypePollingExhausted) {
                    messageType = await getTopicType(ros, topic);
                    if (cancelled || hasReceivedMessage) {
                        return;
                    }
                    if (!messageType) {
                        topicTypePollCount += 1;
                        if (maxTopicTypePolls > 0 && topicTypePollCount >= maxTopicTypePolls) {
                            topicTypePollingExhausted = true;
                        } else {
                            schedulePoll();
                        }
                        return;
                    }
                } else if (!messageType) {
                    return;
                }
                if (topicListener) {
                    return;
                }
                topicListener = new Topic({
                    ros,
                    name: topic,
                    messageType,
                    compression,
                });
                if (subscribeQos) {
                    attachSubscribeQos(topicListener, subscribeQos);
                }
                topicListener.subscribe((msg: unknown) => wrappedHandler(unNestDataFieldIfPrimitive(msg)));
                lastSubscribedAt = Date.now();
                return;
            }

            teardownListener();
            proxyHandler = wrappedHandler;
            controlSocket.subscribe(topic, proxyHandler);
            lastSubscribedAt = Date.now();
        } finally {
            subscribeInFlight = false;
        }
    };

    const start = () => {
        const runFirstAttempt = () => {
            if (!cancelled) {
                void trySubscribe();
            }
        };

        if (initialDelayMs > 0) {
            pollTimer = setTimeout(() => {
                pollTimer = null;
                runFirstAttempt();
            }, initialDelayMs);
        } else {
            runFirstAttempt();
        }

        if (retryUntilFirstMessage) {
            missingRetryTimer = setInterval(() => {
                if (cancelled || hasReceivedMessage || subscribeInFlight) {
                    return;
                }

                if (!topicListener && !proxyHandler) {
                    if (!topicTypePollingExhausted) {
                        void trySubscribe();
                    }
                    return;
                }

                if (
                    resubscribeWhileMissingMs > 0 &&
                    Date.now() - lastSubscribedAt >= resubscribeWhileMissingMs
                ) {
                    teardownListener();
                    void trySubscribe();
                }
            }, retryWhileMissingMs);
        }
    };

    start();

    return () => {
        cancelled = true;
        if (pollTimer != null) {
            clearTimeout(pollTimer);
        }
        clearMissingRetryTimer();
        teardownListener();
    };
}
